import React from 'react';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { A3Seite } from './A3Seite';
import { HANDBUCH_FARBEN } from './stil';
import { formatDatum } from '../../../services/assignmentLogic';
import {
    baueZellen, offeneUeberschneidungen, wirksamerZeitraumDerFassung, zaehleUnbewertet,
} from '../handbuchLogic';
import type { Zelle } from '../handbuchLogic';
import type { KoordinierungPaar } from '../../koordinierung/koordinierungLogic';
import type { Ampel } from '../../koordinierung/types';
import type { GewerkAusPlan, Handbuch, HandbuchGewerk } from '../types';

/**
 * Kapitel „Gefährdungsmatrix" — gegenseitige Gefährdungen der Gewerke.
 *
 * Darstellung portiert aus `pages/editor/GefaehrdungsmatrixPage.tsx` der
 * Quell-App (Zeile = Verursacher, Spalte = Betroffener, Diagonale = Strich).
 * Die DATEN kommen woanders her: nicht mehr aus `Projekt.schnittstellen` und
 * erst recht nicht aus einer KI-Vermutung, sondern aus `koordinierung_paare` —
 * den Bewertungen, die im Modul „Gewerke-Überschneidung" von einem Menschen
 * getroffen und bestätigt wurden.
 *
 * ═══ DER WICHTIGSTE PUNKT DIESES KAPITELS ══════════════════════════════════
 * Eine leere Matrix darf NIE wie eine Entwarnung aussehen. Ein SiGe-Handbuch,
 * das eine Kreuztabelle ohne einen einzigen Eintrag zeigt, wird gelesen als
 * „geprüft, keine gegenseitigen Gefährdungen" — und genau das ist die
 * gefährlichste Aussage, die dieses Dokument treffen kann. Liegt keine
 * Bewertung vor, zeichnet diese Seite deshalb GAR KEINE Matrix, sondern sagt
 * im Klartext, dass die Analyse noch aussteht, und verweist auf das Modul, in
 * dem sie stattfindet.
 *
 * ═══ DREI ZUSTÄNDE, DIE MAN AUSEINANDERHALTEN MUSS ═════════════════════════
 *   • zeitliche Überschneidung UND Bewertung   → die Zelle zeigt die Bewertung
 *   • zeitliche Überschneidung OHNE Bewertung  → die Zelle sagt „noch nicht
 *     bewertet". Sie bleibt farbig, denn die Gefährdung ist zeitlich möglich;
 *     nur beurteilt hat sie niemand.
 *   • keine gemeinsame Zeit                    → grau, und das ist eine echte
 *     Feststellung, kein Datenmangel.
 *
 * Die Seite SCHREIBT NICHTS. Sie liest `handbuch_gewerke` und
 * `koordinierung_paare` und stellt sie dar.
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// Der Rechenteil dieses Blatts liegt in `handbuchLogic.ts`: `baueZellen`,
// `zaehleUnbewertet`, `offeneUeberschneidungen`, `wirksamerZeitraumDerFassung` und vor
// allem `koordinierungsSchluessel` — die Bruecke zwischen den zwei
// Schluesselraeumen. Dort ist sie geprueft; hier waere sie es nicht (das
// Projekt uebersetzt Tests ohne jsdom). Was hier bleibt, ist Darstellung.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ab wie vielen Gewerken die Matrix ein A3-Blatt sprengt.
 *
 * Die Seite schneidet ab (`overflow: hidden` in `A3Seite` — auf Papier gibt es
 * keine Bildlaufleiste), und ein abgeschnittenes Blatt ist im Handbuch
 * schlimmer als ein Hinweis. Zwölf Spalten sind bei 420 mm Breite die Grenze,
 * ab der eine Zelle keine zwei Zeilen Text mehr trägt.
 */
const MATRIX_HOECHSTZAHL = 12;

// ═══════════════════════════════════════════════════════════════════════════
// DARSTELLUNG
// ═══════════════════════════════════════════════════════════════════════════

const AMPEL_FUELLUNG: Record<Ampel, string> = {
    rot: '#fde8e6',
    orange: '#fdf0dc',
    grau: '#f4f4f4',
};

const AMPEL_SCHRIFT: Record<Ampel, string> = {
    rot: HANDBUCH_FARBEN.risikoHoch,
    orange: HANDBUCH_FARBEN.risikoMittel,
    grau: '#999999',
};

const zellenRahmen: React.CSSProperties = {
    border: `1px solid ${HANDBUCH_FARBEN.linie}`,
    padding: '2px 3px',
    verticalAlign: 'top',
    fontSize: '7.5px',
    lineHeight: 1.25,
};

const kopfZelle: React.CSSProperties = {
    ...zellenRahmen,
    background: HANDBUCH_FARBEN.linieStark,
    color: '#ffffff',
    fontWeight: 700,
    textAlign: 'center',
};

/** Zeitraum kurz, ohne Jahrhundert-Ballast: „04.03.–19.03.2026 (16 T.)". */
const fensterText = (von: string | null, bis: string | null, dauerTage: number): string => {
    const a = formatDatum(von);
    const b = formatDatum(bis);
    if (!a || !b) return '';
    return `${a}–${b} (${dauerTage} T.)`;
};

const ZellenInhalt: React.FC<{ zelle: Zelle }> = ({ zelle }) => {
    if (zelle.lage === 'diagonale') {
        return <div style={{ textAlign: 'center', color: '#999999', fontSize: '11px' }}>—</div>;
    }
    if (zelle.lage === 'gleicher_schluessel') {
        return (
            <div style={{ color: HANDBUCH_FARBEN.risikoMittel, fontStyle: 'italic' }}>
                Beide Seiten führt die Koordinierung als EIN Gewerk — dort nicht getrennt bewertbar.
            </div>
        );
    }
    if (zelle.lage === 'ohne_ueberschneidung') {
        return <div style={{ color: '#999999' }}>keine zeitl. Überschneidung</div>;
    }

    return (
        <>
            <div style={{ color: AMPEL_SCHRIFT[zelle.ampel], fontWeight: 700, marginBottom: '2px' }}>
                {fensterText(zelle.von, zelle.bis, zelle.dauerTage) || 'Zeitraum unbekannt'}
            </div>
            {zelle.lage === 'unbewertet' ? (
                /* KEIN „keine Gefährdungen": die Aussage wäre falsch. Es hat
                   nur niemand hingesehen. */
                <div style={{ color: HANDBUCH_FARBEN.risikoHoch, fontWeight: 700 }}>
                    ✗ noch nicht bewertet
                </div>
            ) : zelle.bewertungen.map(b => (
                <div key={b.id} style={{ marginBottom: '2px' }}>
                    <div style={{ color: HANDBUCH_FARBEN.text }}>
                        {b.gefaehrdung}
                        {b.veraltet_seit && (
                            <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}> (überholt)</span>
                        )}
                        {!b.bestaetigt_am && !b.veraltet_seit && (
                            <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}> (unbestätigt)</span>
                        )}
                    </div>
                    {b.massnahme && (
                        <div style={{ color: HANDBUCH_FARBEN.textLeise, fontSize: '7px' }}>
                            <strong>M{b.top ? ` (${b.top})` : ''}:</strong> {b.massnahme}
                        </div>
                    )}
                </div>
            ))}
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════

interface Props {
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Die Gewerkeseiten des Handbuchs — Zeilen und Spalten der Matrix. */
    gewerke: HandbuchGewerk[];
    /** Bewertete Paare aus `koordinierung_paare`. */
    paare: KoordinierungPaar[];
    /**
     * Der Handbuchsatz — er entscheidet, OB der Zeitraum noch aus dem Plan
     * gerechnet werden darf.
     *
     * Die Ampel dieser Matrix haengt an den Zeitraeumen. Griffe eine
     * freigegebene Fassung dafuer auf den laufenden Plan zurueck, wechselte
     * eine Zelle nach einer Terminverschiebung die Farbe — im gleichen
     * Dokument, ohne Historieneintrag. Im Entwurf ist genau das erwuenscht.
     * Die Unterscheidung trifft `wirksamerZeitraumDerFassung`.
     */
    handbuch?: Handbuch | null;
    /** Gewerke der Planfassung — liefert den Zeitraum, wo die Gewerkeseite
     *  keinen eigenen trägt. */
    planGewerke?: GewerkAusPlan[];
    /** `true`, wenn Migration Nr. 40 noch nicht gefahren ist. Der Unterschied
     *  zählt: „noch nichts bewertet" und „die Tabelle gibt es nicht" sehen
     *  gleich aus, meinen aber Gegenteiliges. */
    tabelleFehlt?: boolean;
    /** Sprung ins Modul „Gewerke-Überschneidung". Fehlt der Rückruf, entfällt
     *  der Knopf — der Hinweistext bleibt. */
    onKoordinierungOeffnen?: () => void;
}

/**
 * Der Hinweis, der an die Stelle der Matrix tritt.
 *
 * Er ist kein Platzhalter, sondern die fachliche Aussage der Seite: es liegt
 * keine Beurteilung vor. Die darunter aufgeführten Überschneidungen sind
 * gerechnet und nicht bewertet — sie zeigen, wie viel Arbeit noch aussteht.
 */
const KeineDatenHinweis: React.FC<{
    gewerke: HandbuchGewerk[];
    ausPlan: Map<string, GewerkAusPlan>;
    tabelleFehlt: boolean;
    onKoordinierungOeffnen?: () => void;
}> = ({ gewerke, ausPlan, tabelleFehlt, onKoordinierungOeffnen }) => {
    const offen = offeneUeberschneidungen(gewerke, ausPlan);
    return (
        <div className="flex-1 flex flex-col justify-center" style={{ padding: '0 40px' }}>
            <div
                style={{
                    border: `2px solid ${HANDBUCH_FARBEN.risikoHoch}`,
                    background: '#fdf3f2',
                    padding: '14px 18px',
                }}
            >
                <div
                    className="flex items-center gap-2"
                    style={{ color: HANDBUCH_FARBEN.risikoHoch, fontWeight: 700, fontSize: '13px' }}
                >
                    <ShieldAlert size={18} />
                    Die gegenseitigen Gefährdungen sind noch nicht beurteilt.
                </div>
                <div style={{ fontSize: '10px', marginTop: '8px', lineHeight: 1.5 }}>
                    {tabelleFehlt ? (
                        <>
                            Die Auswertung der Gewerke-Überschneidung steht in dieser Umgebung noch
                            nicht zur Verfügung (Migration Nr.&nbsp;40 ist nicht gefahren). Es liegt
                            deshalb keine einzige Bewertung vor.
                        </>
                    ) : (
                        <>
                            Zu diesem Projekt ist im Modul „Gewerke-Überschneidung" noch keine
                            Bewertung erfasst worden.
                        </>
                    )}
                    <div style={{ marginTop: '6px', fontWeight: 700 }}>
                        Diese Seite ist keine Entwarnung. Eine leere Matrix bedeutet nicht, dass es
                        keine gegenseitigen Gefährdungen gibt — sie bedeutet, dass niemand sie
                        aufgeschrieben hat.
                    </div>
                </div>

                {offen.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '9px' }}>
                        <div style={{ fontWeight: 700, marginBottom: '3px' }}>
                            {offen.length} zeitliche Überschneidung(en) sind bereits aus dem
                            Terminplan errechenbar und warten auf eine Beurteilung:
                        </div>
                        <ul style={{ listStyle: 'disc', paddingLeft: '16px' }}>
                            {offen.slice(0, 8).map(u => (
                                <li key={`${u.a}~${u.b}`}>
                                    {u.a} × {u.b} — {fensterText(u.von, u.bis, u.dauerTage)}
                                </li>
                            ))}
                        </ul>
                        {offen.length > 8 && (
                            <div style={{ marginTop: '2px', color: HANDBUCH_FARBEN.textLeise }}>
                                … und {offen.length - 8} weitere.
                            </div>
                        )}
                    </div>
                )}

                {onKoordinierungOeffnen && (
                    /* Nur am Bildschirm: ein Knopf auf einem gedruckten Blatt
                       ist ein Fleck. */
                    <button
                        type="button"
                        onClick={onKoordinierungOeffnen}
                        className="print:hidden mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded bg-rose-700 text-white text-[12px] font-bold hover:bg-rose-800"
                    >
                        Zur Gewerke-Überschneidung
                        <ArrowRight size={14} />
                    </button>
                )}
            </div>
        </div>
    );
};

export const Gefaehrdungsmatrix: React.FC<Props> = ({
    kopf, fuss, gewerke, paare, handbuch = null, planGewerke = [], tabelleFehlt = false,
    onKoordinierungOeffnen,
}) => {
    const liste = [...(gewerke || [])].sort((a, b) => a.sortierung - b.sortierung);
    const ausPlan = new Map<string, GewerkAusPlan>((planGewerke || []).map(g => [g.gewerk_norm, g]));
    const bewertungen = paare || [];

    /*
     * `baueZellen` und `offeneUeberschneidungen` nehmen den Rueckgriff auf den
     * Plan selbst vor; den Freigabestand des Handbuchs kennen sie nicht. Der
     * Zeitraum wird deshalb EINMAL hier mit `wirksamerZeitraumDerFassung`
     * bestimmt und in die Zeile gelegt — dieselbe Funktion, mit der die
     * Gewerkeseite, der Gesamtzeitplan und das PDF rechnen. Nachgebaut wird
     * nichts.
     *
     * Weitergereicht wird danach eine LEERE Plankarte, damit der Rueckgriff
     * nicht ein zweites Mal stattfindet: bei einer freigegebenen Fassung ist
     * ein leer gebliebenes Ende eine Aussage („bei der Freigabe gab der Plan
     * nichts her") und keine offene Stelle, die der heutige Plan fuellen
     * duerfte. Im Entwurf steht der Planwert bereits in der Kopie, dort
     * aendert die leere Karte nichts.
     */
    const ohnePlan = new Map<string, GewerkAusPlan>();
    const listeDerFassung = liste.map(g => {
        const z = wirksamerZeitraumDerFassung(g, ausPlan, handbuch);
        return { ...g, zeitraum_von: z.von, zeitraum_bis: z.bis };
    });

    const kopfzeile = (
        <div style={{ marginBottom: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>
                GEFÄHRDUNGSMATRIX — GEGENSEITIGE GEFÄHRDUNGEN
            </div>
            <div style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                Zeile = Verursacher → Spalte = Betroffener. Quelle: Modul „Gewerke-Überschneidung"
                (§ 3 BaustellV), nicht geschätzt.
            </div>
        </div>
    );

    // ─── Fall 1: keine einzige Bewertung ───────────────────────────────────
    if (bewertungen.length === 0) {
        return (
            <A3Seite kopf={kopf} fuss={fuss}>
                {kopfzeile}
                <KeineDatenHinweis
                    gewerke={listeDerFassung}
                    ausPlan={ohnePlan}
                    tabelleFehlt={tabelleFehlt}
                    onKoordinierungOeffnen={onKoordinierungOeffnen}
                />
            </A3Seite>
        );
    }

    // ─── Fall 2: zu wenige Gewerkeseiten für eine Kreuztabelle ─────────────
    if (liste.length < 2) {
        return (
            <A3Seite kopf={kopf} fuss={fuss}>
                {kopfzeile}
                <div className="flex-1 flex items-center justify-center">
                    <div style={{ fontSize: '11px', color: HANDBUCH_FARBEN.textLeise, textAlign: 'center' }}>
                        Für eine Kreuzmatrix werden mindestens zwei Gewerkeseiten gebraucht.<br />
                        Angelegt {liste.length === 1 ? 'ist bisher eine' : 'ist bisher keine'} —
                        die Gewerke werden aus der Planfassung übernommen.
                    </div>
                </div>
            </A3Seite>
        );
    }

    const zellen = baueZellen(listeDerFassung, ohnePlan, bewertungen);
    const unbewertet = zaehleUnbewertet(zellen);

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            {kopfzeile}

            {unbewertet > 0 && (
                <div
                    style={{
                        border: `1px solid ${HANDBUCH_FARBEN.risikoHoch}`,
                        background: '#fdf3f2',
                        color: HANDBUCH_FARBEN.risikoHoch,
                        padding: '3px 6px',
                        fontSize: '8.5px',
                        fontWeight: 700,
                        marginBottom: '5px',
                    }}
                >
                    {unbewertet} Gewerkepaar(e) treffen zeitlich zusammen, ohne dass eine Gefährdung
                    beurteilt wurde — in der Matrix mit „✗ noch nicht bewertet" gekennzeichnet.
                </div>
            )}

            {liste.length > MATRIX_HOECHSTZAHL && (
                <div
                    style={{
                        fontSize: '8px',
                        color: HANDBUCH_FARBEN.risikoMittel,
                        marginBottom: '4px',
                        fontStyle: 'italic',
                    }}
                >
                    {liste.length} Gewerke: ab {MATRIX_HOECHSTZAHL + 1} Spalten schneidet das A3-Blatt
                    ab. Gewerkeseiten zusammenführen oder das Analyse-PDF der Koordinierung beilegen.
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-hidden">
                <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '120px' }} />
                        {liste.map(g => <col key={g.id} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            {/* Die Kopfzeile trägt nur die laufende Nummer. Ausgeschriebene
                                Gewerksnamen brauchten gedrehte Schrift oder 4 cm breite
                                Spalten — beides wird ab acht Gewerken unlesbar. Dieselbe
                                Lösung wie in der Matrix des Koordinierungsmoduls. */}
                            <th style={{ ...kopfZelle, textAlign: 'left' }}>
                                Verursacher ↓ / Betroffener →
                            </th>
                            {liste.map((g, i) => (
                                <th key={g.id} style={kopfZelle} title={g.gewerk_label}>
                                    {i + 1}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {liste.map((zeile, i) => (
                            <tr key={zeile.id}>
                                <td style={{ ...kopfZelle, textAlign: 'left', fontWeight: 700 }}>
                                    {i + 1}. {zeile.gewerk_label}
                                    {zeile.firma && (
                                        <div style={{ fontWeight: 400, opacity: 0.8 }}>{zeile.firma}</div>
                                    )}
                                </td>
                                {liste.map((spalte, j) => (
                                    <td
                                        key={spalte.id}
                                        style={{
                                            ...zellenRahmen,
                                            background: zellen[i][j].lage === 'diagonale'
                                                ? '#dcdcdc'
                                                : AMPEL_FUELLUNG[zellen[i][j].ampel],
                                        }}
                                    >
                                        <ZellenInhalt zelle={zellen[i][j]} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Legende. Die Farbe allein trägt keine Aussage — das Blatt wird
                schwarz-weiß im Baucontainer ausgedruckt, und Rot und Grün sind
                für einen Teil der Leser ohnehin dasselbe. Deshalb steht in
                jeder Zelle Text. */}
            <div
                style={{
                    borderTop: `1px solid ${HANDBUCH_FARBEN.linie}`,
                    marginTop: '5px',
                    paddingTop: '4px',
                    fontSize: '7.5px',
                    color: HANDBUCH_FARBEN.textLeise,
                    display: 'flex',
                    gap: '18px',
                    flexWrap: 'wrap',
                }}
            >
                <span><strong>M</strong> = Maßnahme · <strong>T</strong> technisch ·{' '}
                    <strong>O</strong> organisatorisch · <strong>P</strong> personenbezogen</span>
                <span style={{ color: HANDBUCH_FARBEN.risikoHoch }}>■ mehr als 4 Wochen gemeinsam</span>
                <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}>■ bis 4 Wochen gemeinsam</span>
                <span>■ keine gemeinsame Zeit</span>
                <span>
                    Die Koordinierung bewertet Paare ungerichtet; jede Bewertung steht deshalb in
                    beiden Feldern.
                </span>
            </div>
        </A3Seite>
    );
};
